import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import "@fontsource/inter";
import moment from "moment";
import type { CartContextType } from "@/features/marketplace";
import type { CartItemWithPrice, CollectionWithPrice } from "@/types/api";

// helper so that we can add to cart from anywhere and not need to worry about manually handling state in each component that needs it
export function createInCartHandler(
  item: CollectionWithPrice,
  inCart: boolean,
  setInCart: React.Dispatch<React.SetStateAction<boolean>>,
  ctx: CartContextType
) {
  return (e: React.MouseEvent) => {
    e.stopPropagation();

    e.stopPropagation();
    inCart ? ctx.removeFromCart(item) : ctx.addToCart(item);
    setInCart(prev => !prev);
  }
}

export function toCapital(s: string) {
  return s[0].toUpperCase() + s.slice(1);
}

export enum DeviceType {
  Windows = "windows",
  MacOs = "macos"
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function Inter(/*{ subsets = ["latin"]} = {}*/) {
  return {
    className: "font-inter",
    fontFamily: "'Inter', sans-serif",
  };
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

// Given a string in the following format 'Month day, year'
// ex 'June 14, 2025'
// returns the ISO 
function humanDateToISO(input: string): string {
  const month = input.slice(0, input.indexOf(" "));
  input = input.slice(input.indexOf(" ") + 1);

  const day = input.slice(0, input.indexOf(","));
  input = input.slice(input.indexOf(" ") + 1);

  const year = input;

  console.log(month, year, day);

  if (!MONTHS.includes(month)) {
    throw new Error("Trouble Parsing End Date");
  }

  return year + "-" + `0${MONTHS.indexOf(month) + 1}` + "-" + day;
}

// Given a string in the following format 'Month day, year'
// ex 'June 14, 2025'
// returns the days until this date
export function daysUntil(endDateString: string): number {
  const endDate = new Date(humanDateToISO(endDateString)).getTime();
  const currentDate = new Date().getTime();
  return Math.round((endDate - currentDate) / (1000 * 60 * 60 * 24));
}

export function numTrialDays(endDate: moment.MomentInput) {
  const trial_end = moment(endDate, "MMM DD YYYY");
  const today = moment.utc();
  const days_left = trial_end.diff(today, "days") + 1; //include start date
  return days_left;
}

export function formatLicenseCodes(raw: string): string {
  const cleaned = raw.replace(/MUS/gi, '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  const groups: string[] = [];

  for (let i = 0; i < cleaned.length; i += 4) {
    groups.push(cleaned.slice(i, i + 4));
  }

  const lines: string[] = [];
  for (let i = 0; i < groups.length; i += 4) {
    lines.push("MUS-" + groups.slice(i, i + 4).join('-'));
  }

  return lines.join('\n');
}

export function validateLicenseInputs(input: string): boolean {
  return true;
};

// assumes correctly formatted 
export const CODE_FORMAT = "MUS-XXXX-XXXX-XXXX-XXXX";
export function extractLicenseCodes(input: string): string[] {
  const codes = input.split("\n");
  // TODO correct validation and write sensible errors
  // codes.forEach((code) => {
  //   if (code.length != CODE_FORMAT.length) {
  //     throw new Error("Code length mismatch ");
  //   }
  // });
  return codes;
};

export function capitalize(input: string): string {
  if (!input) return "";
  return input.charAt(0).toUpperCase() + input.slice(1);
}

// Check and set takes a setState variable function, the data associated with the state variabl, and an error.
// It checks the error and throws if its not null, and sets the state if no error is thrown
// This is good for a .then pattern that takes the { data, error } as arguments
// client.GET('/user/billing', {}).then(({data, error}) => checkAndSet(setBillingStatus, data, error)).catch((e) => { console.log(e) }),
const checkAndSet = <T>(
  set: React.Dispatch<React.SetStateAction<T>>,
  data: T,
  error: any
) => {
  if (error != null) {
    throw new Error(error);
  }

  set(data);
};

export { checkAndSet };

// NOTE: might not actually need this Intl exposes this for us already
export const NO_MINOR_FORMAT = ["JPY", "KRW", "VND", "IDR", "UGX", "CLP", "PYG", "ISK", "RWF", "BYN", "KMF", "MGA"];

export function formatCurrency(total: number, currency: string, locale: string = 'en-us') {
  const formatter = Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency.toUpperCase(),
  });

  const { minimumFractionDigits } = formatter.resolvedOptions();
  const factor = 10 ** (minimumFractionDigits != undefined ? minimumFractionDigits : 2);

  const amount = total / factor;

  return formatter.format(amount);
}

export function isBefore(str: string): boolean {
  return moment(str).isBefore(moment());
}

export function isAfter(str: string): boolean {
  return moment(str).isAfter(moment());
}

export function isWithin(str: string, days: number): boolean {
  return moment(str).isBetween(moment(), moment().add(days, "days"));
}

export function parseQueryParamArr(params: string): string[] {
  return params.split(",");
}

export function codeIsActive(startDate?: string, endDate?: string): boolean {
  const now = Date.now();
  if (startDate && new Date(startDate).getTime() > now) return false;
  if (endDate && new Date(endDate).getTime() < now) return false;

  return true;
}
